/**
 * The always-on webcam indicator for the desktop exam runner.
 *
 * The student is being recorded for the whole exam but, once it starts, has no
 * self-view — so they cannot tell whether the webcam is still working, and a
 * silent stop goes unnoticed. This pins a small self-view to the corner for the
 * entire exam with an unmistakable state:
 *
 *   recording     — live self-view, red REC dot, elapsed time
 *   reconnecting  — "Reconnecting camera…" over the frame (self-heal in flight)
 *   stopped       — a red "RECORDING STOPPED — check your camera" panel
 *
 * It never simply disappears while recording is wanted.
 */
import { useEffect, useRef } from "react";
import { AlertTriangle, CloudOff, Monitor, Video } from "lucide-react";
import { cn } from "@/lib/cn";
import { formatDuration } from "@/lib/exam/serverClock";
import { formatMB, type RecordingUiState, type UploadHealth } from "@/lib/recorder";

export function RecordingIndicator({
  stream,
  state,
  elapsedMs,
  screenState,
  upload,
  onRestartCamera,
  onReshareScreen,
}: {
  stream: MediaStream | null;
  state: RecordingUiState;
  elapsedMs: number | null;
  /** Screen-capture status, shown as a second line. Omit to hide it. */
  screenState?: RecordingUiState;
  /** Combined upload health (webcam + screen) — surfaces silent upload stalls. */
  upload?: UploadHealth;
  /**
   * Recovery the student can actually perform.
   *
   * Without these the warning was a dead end: it said recording had stopped and
   * offered nothing to do about it, so a student who noticed was no better off
   * than one who did not. A camera restart needs no permission prompt; a screen
   * re-share does, which is why it must be a button and cannot be automatic.
   */
  onRestartCamera?: () => void;
  onReshareScreen?: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.srcObject = stream ?? null;
    if (stream) void el.play().catch(() => undefined);
  }, [stream]);

  // Before the exam starts there is nothing to assert.
  if (state === "idle") return null;

  return (
    <div className="fixed bottom-4 right-4 z-30 w-44 overflow-hidden rounded-md border border-border-strong bg-ink">
      <div className="relative aspect-[4/3] w-full">
        <video
          ref={videoRef}
          muted
          playsInline
          className="size-full object-cover"
        />

        {/* REC / state badge, top-left, legible over the video. */}
        <div className="absolute left-1.5 top-1.5 flex items-center gap-1 rounded-sm border border-border bg-surface/90 px-1.5 py-0.5">
          <span
            className={cn(
              "size-2 rounded-full",
              state === "recording"
                ? "bg-violation"
                : state === "reconnecting"
                  ? "bg-review"
                  : "bg-ink-faint",
            )}
          />
          <span className="text-2xs font-semibold tracking-wide text-ink">
            {state === "recording"
              ? "REC"
              : state === "reconnecting"
                ? "···"
                : "OFF"}
          </span>
        </div>

        {/* Elapsed recording time, bottom-right. */}
        {elapsedMs !== null && state !== "stopped" ? (
          <span className="tabular absolute bottom-1.5 right-1.5 rounded-sm border border-border bg-surface/90 px-1.5 py-0.5 text-2xs font-medium text-ink">
            {formatDuration(elapsedMs)}
          </span>
        ) : null}

        {/* Reconnecting overlay — transient, calm. */}
        {state === "reconnecting" ? (
          <div className="absolute inset-0 flex items-center justify-center bg-surface/85 px-2 text-center">
            <span className="text-2xs font-medium text-review">
              Reconnecting camera…
            </span>
          </div>
        ) : null}

        {/* Stopped — unmistakable, needs the student to act. */}
        {state === "stopped" ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-violation px-2 text-center">
            <AlertTriangle className="size-4 text-surface" />
            <span className="text-2xs font-semibold text-surface">
              Recording stopped
            </span>
            {onRestartCamera ? (
              <button
                type="button"
                onClick={onRestartCamera}
                className="mt-0.5 rounded-sm bg-surface px-2 py-0.5 text-2xs font-semibold text-violation"
              >
                Restart camera
              </button>
            ) : (
              <span className="text-2xs text-surface/90">Check your camera</span>
            )}
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-0.5 border-t border-border bg-surface px-2 py-1">
        <SourceLine
          icon={<Video className="size-3" />}
          label="Webcam"
          state={state}
        />
        {screenState ? (
          <SourceLine
            icon={<Monitor className="size-3" />}
            label="Screen"
            state={screenState}
          />
        ) : null}
      </div>

      {upload ? (
        <UploadBar
          upload={upload}
          onRestartCamera={onRestartCamera}
          onReshareScreen={onReshareScreen}
        />
      ) : null}
    </div>
  );
}

/**
 * The upload-health strip. The whole point is that a SILENT stall — footage not
 * reaching the server — is now loud: it turns red and says so.
 */
function UploadBar({
  upload,
  onRestartCamera,
  onReshareScreen,
}: {
  upload: UploadHealth;
  onRestartCamera?: () => void;
  onReshareScreen?: () => void;
}) {
  // The worst state, and the one the old indicator could not show at all: no
  // video is being produced, so there is nothing queued and every number below
  // looks healthy. Ranked above "stalled" for exactly that reason.
  if (upload.state === "not-recording") {
    return (
      <div className="border-t border-violation/40 bg-violation px-2 py-1.5 text-surface">
        <p className="flex items-center gap-1 text-2xs font-semibold">
          <AlertTriangle className="size-3.5 shrink-0" />
          Nothing is being recorded
        </p>
        <p className="text-2xs text-surface/90">
          Your answers are safe. Restart the recording now.
        </p>
        <div className="mt-1 flex gap-1">
          {onRestartCamera ? (
            <button
              type="button"
              onClick={onRestartCamera}
              className="rounded-sm bg-surface px-2 py-0.5 text-2xs font-semibold text-violation"
            >
              Restart camera
            </button>
          ) : null}
          {onReshareScreen ? (
            <button
              type="button"
              onClick={onReshareScreen}
              className="rounded-sm bg-surface px-2 py-0.5 text-2xs font-semibold text-violation"
            >
              Re-share screen
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  if (upload.state === "stalled") {
    return (
      <div className="border-t border-violation/40 bg-violation px-2 py-1.5 text-surface">
        <p className="flex items-center gap-1 text-2xs font-semibold">
          <CloudOff className="size-3.5 shrink-0" />
          Recording is NOT reaching the server
        </p>
        <p className="text-2xs text-surface/90">
          Check your connection — keep this page open.{" "}
          <span className="tabular">{upload.pending}</span> segment
          {upload.pending === 1 ? "" : "s"} waiting
          {upload.pendingBytes > 0 ? ` (${formatMB(upload.pendingBytes)})` : ""}.
        </p>
      </div>
    );
  }

  if (upload.state === "behind") {
    return (
      <div className="flex items-center gap-1 border-t border-border bg-review-soft px-2 py-1 text-2xs text-review">
        <AlertTriangle className="size-3 shrink-0" />
        Upload is behind —{" "}
        <span className="tabular font-medium">{upload.pending}</span> pending. Keep
        this page open.
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-2 border-t border-border bg-surface px-2 py-1 text-2xs text-ink-faint">
      <span className="tabular">
        Uploaded {upload.uploaded}
        {upload.pending > 0 ? ` · ${upload.pending} pending` : ""}
      </span>
      {/* A recovered wedge means footage was at risk even though it is fine
          now, so it is shown rather than swallowed. */}
      {upload.wedgeRecoveries > 0 ? (
        <span className="tabular text-review" title="Upload stalled and restarted">
          ⟳ {upload.wedgeRecoveries}
        </span>
      ) : null}
    </div>
  );
}

function SourceLine({
  icon,
  label,
  state,
}: {
  icon: React.ReactNode;
  label: string;
  state: RecordingUiState;
}) {
  const text =
    state === "recording"
      ? "recording"
      : state === "reconnecting"
        ? "reconnecting…"
        : state === "stopped"
          ? "stopped"
          : "off";
  return (
    <span
      className={cn(
        "flex items-center gap-1 text-2xs",
        state === "recording"
          ? "text-ink-muted"
          : state === "reconnecting"
            ? "text-review"
            : state === "stopped"
              ? "text-violation"
              : "text-ink-faint",
      )}
    >
      <span
        className={cn(
          state === "recording"
            ? "text-violation"
            : state === "reconnecting"
              ? "text-review"
              : state === "stopped"
                ? "text-violation"
                : "text-ink-faint",
        )}
      >
        {icon}
      </span>
      {label} {text}
    </span>
  );
}
